import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { addDocument } from '../services/firebaseService';
import { trackPageView, trackFormSubmission } from '../services/analyticsService';
import { sendQuoteEmail, initEmailJS } from '../services/emailService';
import phoneIcon from '../icons/phone_icon.png';
import whatsappIcon from '../icons/whatsapp_icon.png';
import { services } from '../data/services';
const firstImage = require('../icons/first_image.JPG');

const AnimatedCounter: React.FC<{ end: number; suffix?: string; duration?: number }> = ({ end, suffix = '', duration = 2000 }) => {
  const [count, setCount] = useState(0);
  const [isVisible, setIsVisible] = useState(false);
  const elementRef = React.useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.1 }
    );

    if (elementRef.current) {
      observer.observe(elementRef.current);
    }

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!isVisible) return;

    let startTime: number | null = null;
    let animationFrame: number;

    const animate = (timestamp: number) => {
      if (!startTime) startTime = timestamp;
      const progress = timestamp - startTime;
      const percentage = Math.min(progress / duration, 1);

      setCount(Math.floor(percentage * end));

      if (percentage < 1) {
        animationFrame = requestAnimationFrame(animate);
      } else {
        setCount(end);
      }
    };

    animationFrame = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(animationFrame);
  }, [isVisible, end, duration]);

  return <div ref={elementRef}>{count.toLocaleString()}{suffix}</div>;
};

interface QuoteFormData {
  recoveryType: string;
  collectionPostcode: string;
  deliveryPostcode: string;
  phoneNumber: string;
  regNumber: string;
  make: string;
  model: string;
  date: string;
}

const Homepage: React.FC = () => {
  const [formData, setFormData] = useState<QuoteFormData>({
    recoveryType: 'Breakdown Recovery',
    collectionPostcode: '',
    deliveryPostcode: '',
    phoneNumber: '',
    regNumber: '',
    make: '',
    model: '',
    date: ''
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const navigate = useNavigate();

  // SEO Meta Tags
  useEffect(() => {
    try {
      // Set document title
      document.title = 'Cheapest Car Recovery in UK & Birmingham | 24/7 Breakdown Recovery Services';

      // Create meta tags
      const metaTags = [
        { name: 'description', content: 'Cheapest Car Recovery in UK & Birmingham - Professional 24/7 breakdown recovery services. Cheap car recovery in UK with fastest response times. Emergency roadside assistance, accident recovery across England, Scotland, Wales. Call now!' },
        { name: 'keywords', content: 'cheapest car recovery in uk, cheapest car recovery in birmingham, cheap car recovery in uk, car recovery birmingham, breakdown recovery uk, vehicle recovery services, roadside assistance uk, emergency car recovery, accident recovery uk, towing services birmingham, 24/7 car recovery, motorway recovery uk' },
        { name: 'author', content: 'Cheapest Car Recovery UK' },
        { name: 'robots', content: 'index, follow' },
        { name: 'viewport', content: 'width=device-width, initial-scale=1.0' },
        { property: 'og:title', content: 'Cheapest Car Recovery in UK & Birmingham | 24/7 Emergency Services' },
        { property: 'og:description', content: 'Cheapest Car Recovery in UK & Birmingham - Professional 24/7 breakdown recovery services. Cheap car recovery in UK with fastest response times. Emergency roadside assistance across England, Scotland, Wales. Get instant quote!' },
        { property: 'og:type', content: 'website' },
        { property: 'og:url', content: 'https://cheapestcarrecovery.uk' },
        { property: 'og:image', content: 'https://cheapestcarrecovery.uk/my-logo.png' },
        { property: 'og:locale', content: 'en_GB' },
        { name: 'twitter:card', content: 'summary_large_image' },
        { name: 'twitter:title', content: 'Cheapest Car Recovery in UK & Birmingham | 24/7 Services' },
        { name: 'twitter:description', content: 'Cheapest Car Recovery in UK & Birmingham - Professional 24/7 breakdown recovery services. Cheap car recovery in UK with fastest response times.' },
        { name: 'geo.region', content: 'GB-BIR' },
        { name: 'geo.placename', content: 'Birmingham, United Kingdom' },
        { name: 'geo.position', content: '52.4862;-1.8904' },
        { name: 'ICBM', content: '52.4862, -1.8904' }
      ];

      // Remove existing meta tags and add new ones
      metaTags.forEach(tag => {
        const existingTag = document.querySelector(`meta[name="${tag.name}"], meta[property="${tag.property}"]`);
        if (existingTag) {
          existingTag.remove();
        }

        const metaTag = document.createElement('meta');
        if (tag.name) metaTag.setAttribute('name', tag.name);
        if (tag.property) metaTag.setAttribute('property', tag.property);
        metaTag.setAttribute('content', tag.content);
        document.head.appendChild(metaTag);
      });

      // Add structured data for local business
      const structuredData = {
        "@context": "https://schema.org",
        "@type": "LocalBusiness",
        "name": "Cheapest Car Recovery in UK",
        "alternateName": ["Cheapest Car Recovery in Birmingham", "Cheap Car Recovery in UK"],
        "description": "Cheapest Car Recovery in UK & Birmingham - Professional 24/7 breakdown recovery services. Cheap car recovery in UK with fastest response times across England, Scotland, Wales & Northern Ireland.",
        "url": "https://cheapestcarrecovery.uk",
        "telephone": "+447432387241",
        "email": "admin@cheapestcarrecovery.uk",
        "address": {
          "@type": "PostalAddress",
          "addressLocality": "Birmingham",
          "addressCountry": "GB"
        },
        "geo": {
          "@type": "GeoCoordinates",
          "latitude": "52.4862",
          "longitude": "-1.8904"
        },
        "openingHours": "Mo-Su 00:00-23:59",
        "priceRange": "££",
        "serviceArea": {
          "@type": "GeoCircle",
          "geoMidpoint": {
            "@type": "GeoCoordinates",
            "latitude": "52.4862",
            "longitude": "-1.8904"
          },
          "geoRadius": "50000"
        },
        "hasOfferCatalog": {
          "@type": "OfferCatalog",
          "name": "Cheapest Car Recovery Services in UK",
          "itemListElement": [
            {
              "@type": "Offer",
              "itemOffered": {
                "@type": "Service",
                "name": "Cheapest Car Recovery in UK",
                "description": "Cheapest car recovery in UK with 24/7 breakdown recovery services across England, Scotland, Wales & Northern Ireland"
              }
            },
            {
              "@type": "Offer",
              "itemOffered": {
                "@type": "Service",
                "name": "Cheapest Car Recovery in Birmingham",
                "description": "Cheapest car recovery in Birmingham with professional breakdown recovery and emergency roadside assistance"
              }
            },
            {
              "@type": "Offer",
              "itemOffered": {
                "@type": "Service",
                "name": "Cheap Car Recovery in UK",
                "description": "Cheap car recovery in UK - affordable breakdown recovery, accident recovery, and vehicle transportation services"
              }
            },
            {
              "@type": "Offer",
              "itemOffered": {
                "@type": "Service",
                "name": "Emergency Roadside Assistance",
                "description": "24/7 emergency roadside assistance - jump start, flat tyre, fuel delivery, and breakdown help across UK"
              }
            }
          ]
        },
        "areaServed": [
          {
            "@type": "City",
            "name": "Birmingham"
          },
          {
            "@type": "Country",
            "name": "United Kingdom"
          },
          {
            "@type": "State",
            "name": "England"
          },
          {
            "@type": "State",
            "name": "Scotland"
          },
          {
            "@type": "State",
            "name": "Wales"
          },
          {
            "@type": "State",
            "name": "Northern Ireland"
          }
        ]
      };

      // Add structured data script
      const existingScript = document.querySelector('script[type="application/ld+json"]');
      if (existingScript) {
        existingScript.remove();
      }

      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.textContent = JSON.stringify(structuredData);
      document.head.appendChild(script);

      // Track homepage view
      trackPageView('/');

      // Initialize EmailJS for form submissions
      initEmailJS();

    } catch (err) {
      console.error('Error setting SEO meta tags:', err);
    }
  }, []);

  // Scroll animations for service boxes
  useEffect(() => {
    const observerOptions = {
      threshold: 0.1,
      rootMargin: '0px 0px -50px 0px'
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('visible');
        }
      });
    }, observerOptions);

    const animatedElements = document.querySelectorAll('.scroll-animate-left');
    animatedElements.forEach(el => observer.observe(el));

    return () => {
      animatedElements.forEach(el => observer.unobserve(el));
    };
  }, []);

  const handleInputChange = (field: keyof QuoteFormData, value: string) => {
    try {
      setFormData(prev => ({
        ...prev,
        [field]: value
      }));
    } catch (err) {
      console.error('Error updating form data:', err);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      console.log('🚀 Starting form submission process');

      // Save to Firebase
      await addDocument('quotes', {
        ...formData,
        timestamp: new Date().toISOString(),
        status: 'pending'
      });
      console.log('✅ Quote saved to Firebase');

      // Send email notification
      await sendQuoteEmail(formData);
      console.log('✅ Email notification sent');

      // Track form submission
      await trackFormSubmission('/');
      console.log('✅ Form submission tracked');

      setFormData({
        recoveryType: 'Breakdown Recovery',
        collectionPostcode: '',
        deliveryPostcode: '',
        phoneNumber: '',
        regNumber: '',
        make: '',
        model: '',
        date: ''
      });

      navigate('/form-success');
    } catch (err) {
      console.error('❌ Error submitting quote:', err);
      setError('Failed to submit quote. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Removed local services array as it's now imported from data/services

  const responsiveStyles = `
    .scroll-animate-left {
      opacity: 0;
      transform: translateX(-50px);
      transition: all 0.8s cubic-bezier(0.4, 0, 0.2, 1);
    }

    .scroll-animate-left.visible {
      opacity: 1;
      transform: translateX(0);
    }

    .mobile-hero-heading, .mobile-hero-image-container {
      display: none !important;
    }

    @media (max-width: 768px) {
      .hero-content {
        flex-direction: column !important;
        text-align: center;
      }
      .hero-text {
        padding-right: 0 !important;
        margin-bottom: 30px;
      }
      .hero-title {
        font-size: 36px !important;
      }
      .section-flex {
        flex-direction: column !important;
      }
      .section-text, .section-image {
        padding: 0 !important;
        margin-bottom: 30px;
      }
      .form-grid {
        grid-template-columns: 1fr !important;
      }
      .services-grid {
        grid-template-columns: 1fr !important;
      }
      .stats-grid {
        grid-template-columns: repeat(2, 1fr) !important;
      }
      .trust-grid {
        grid-template-columns: 1fr !important;
      }
      .vision-grid {
        grid-template-columns: 1fr !important;
      }
      
      .header-nav {
        display: none !important;
      }
      
      .mobile-menu-icon {
        display: block !important;
      }
      
      .header-title {
        font-size: 12px !important;
        line-height: 1.2;
      }
      
      .mobile-menu {
        display: none;
        position: fixed;
        top: 70px;
        left: 0;
        right: 0;
        background: white;
        box-shadow: 0 4px 20px rgba(0,0,0,0.15);
        z-index: 1001;
        padding: 20px;
        transform: translateY(-100%);
        transition: transform 0.3s ease;
      }
      
      .mobile-menu.open {
        display: block;
        transform: translateY(0);
      }
      
      .desktop-get-quote {
        display: none !important;
      }

      /* Mobile-first layout - Form appears before hero on mobile */
      .mobile-form-first {
        order: -1 !important;
        background: white !important;
        padding: 0 !important;
        margin: 0 !important;
        min-height: 100vh !important;
        display: flex !important;
        flex-direction: column !important;
        justify-content: center !important;
      }
      
      .mobile-form-first > div {
        max-width: 100% !important;
        width: 100% !important;
        margin: 0 !important;
        padding: 0 !important;
      }

      .mobile-hero-heading {
        display: none !important;
        text-align: center !important;
        margin-bottom: 25px !important;
      }

      .mobile-hero-image-container {
        display: flex !important;
        justify-content: center !important;
        text-align: center !important;
        margin-bottom: 0 !important;
        padding: 0 !important;
        position: relative !important;
        z-index: 1 !important;
      }

      .mobile-hero-title {
        font-size: 26px !important;
        font-weight: 900 !important;
        color: #333 !important;
        margin: 0 0 10px 0 !important;
        text-transform: uppercase !important;
        letter-spacing: 1px !important;
        text-shadow: 2px 2px 4px rgba(0,0,0,0.1) !important;
      }

      .mobile-hero-subtitle {
        font-size: 16px !important;
        color: #555 !important;
        margin: 0 !important;
        font-weight: 600 !important;
      }

      .mobile-form-container {
        background: rgba(255, 255, 255, 0.98) !important;
        backdrop-filter: blur(15px) !important;
        border-radius: 0 !important;
        padding: 25px !important;
        margin: -220px auto 30px auto !important;
        max-width: 100% !important;
        width: 100% !important;
        box-sizing: border-box !important;
        box-shadow: 0 25px 50px rgba(0,0,0,0.15), 0 0 0 2px rgba(255,255,255,0.3) !important;
        border: none !important;
        border-left: none !important;
        border-right: none !important;
        position: relative !important;
        z-index: 10 !important;
        overflow: hidden !important;
      }

      .mobile-form-container::before {
        content: '' !important;
        position: absolute !important;
        top: 0 !important;
        left: 0 !important;
        right: 0 !important;
        height: 3px !important;
        background: white !important;
      }

      .form-input-mobile {
        border: 2px solid #e0e0e0 !important;
        border-radius: 12px !important;
        padding: 14px !important;
        font-size: 16px !important;
        transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1) !important;
        background: rgba(255, 255, 255, 0.9) !important;
        width: 100% !important;
        box-sizing: border-box !important;
      }

      .form-input-mobile:focus {
        border-color: #FFF222 !important;
        box-shadow: 0 0 0 3px rgba(255, 242, 34, 0.3) !important;
        background: white !important;
        transform: scale(1.02) !important;
        outline: none !important;
      }

      .form-label-mobile {
        font-weight: 700 !important;
        color: #333 !important;
        margin-bottom: 8px !important;
        font-size: 14px !important;
        text-transform: uppercase !important;
        letter-spacing: 0.5px !important;
        display: block !important;
      }

      .form-grid-mobile {
        display: grid !important;
        grid-template-columns: 1fr 1fr !important;
        gap: 15px !important;
        margin-bottom: 25px !important;
      }

      .form-field-full {
        grid-column: 1 / -1 !important;
      }

      .submit-button-mobile {
        background: linear-gradient(135deg, #FFF222 0%, #FFE55C 100%) !important;
        border: 3px solid #333 !important;
        border-radius: 15px !important;
        padding: 16px 30px !important;
        font-size: 18px !important;
        font-weight: 700 !important;
        color: #333 !important;
        cursor: pointer !important;
        transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1) !important;
        text-transform: uppercase !important;
        letter-spacing: 0.5px !important;
        box-shadow: 0 6px 15px rgba(0,0,0,0.2) !important;
        width: 100% !important;
      }

      .submit-button-mobile:hover {
        transform: translateY(-3px) !important;
        box-shadow: 0 8px 25px rgba(0,0,0,0.25) !important;
      }

      .submit-button-mobile:active {
        transform: translateY(0) !important;
      }

      .form-title-mobile {
        font-size: 24px !important;
        font-weight: 800 !important;
        text-align: center !important;
        margin-bottom: 10px !important;
        color: #333 !important;
        text-transform: uppercase !important;
        letter-spacing: 0.5px !important;
      }

      .form-subtitle-mobile {
        text-align: center !important;
        color: #666 !important;
        margin-bottom: 25px !important;
        font-size: 14px !important;
        line-height: 1.4 !important;
      }

      .hero-mobile-order {
        display: none !important;
      }

      /* Floating Action Buttons */
      .floating-buttons {
        position: fixed !important;
        bottom: 20px !important;
        right: 20px !important;
        display: flex !important;
        flex-direction: column !important;
        gap: 15px !important;
        z-index: 1000 !important;
      }

      .floating-btn {
        width: 60px !important;
        height: 60px !important;
        border-radius: 50% !important;
        border: none !important;
        display: flex !important;
        align-items: center !important;
        justify-content: center !important;
        font-size: 24px !important;
        color: white !important;
        text-decoration: none !important;
        box-shadow: 0 4px 20px rgba(0,0,0,0.3) !important;
        transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1) !important;
        cursor: pointer !important;
      }

      .floating-btn:hover {
        transform: scale(1.1) translateY(-2px) !important;
        box-shadow: 0 6px 25px rgba(0,0,0,0.4) !important;
      }

      .floating-btn:active {
        transform: scale(0.95) !important;
      }

      .call-btn {
        background: linear-gradient(135deg, #FFF222, #FFE55C) !important;
        border: 2px solid #333 !important;
      }

      .whatsapp-btn {
        background: linear-gradient(135deg, #25D366, #25D366) !important;
                border: 2px solid #333 !important;

      }

      .floating-btn-text {
        position: absolute !important;
        right: 70px !important;
        background: rgba(0,0,0,0.8) !important;
        color: white !important;
        padding: 8px 12px !important;
        border-radius: 20px !important;
        font-size: 12px !important;
        white-space: nowrap !important;
        opacity: 0 !important;
        transform: translateX(10px) !important;
        transition: all 0.3s ease !important;
        pointer-events: none !important;
      }

      .floating-btn:hover .floating-btn-text {
        opacity: 1 !important;
        transform: translateX(0) !important;
      }

      .floating-icon {
        width: 28px !important;
        height: 28px !important;
        transition: transform 0.3s ease !important;
      }

      .call-btn .floating-icon {
        filter: brightness(0) !important;
      }

      .whatsapp-btn .floating-icon {
        filter: invert(100%) sepia(100%) saturate(0%) 
        hue-rotate(288deg) brightness(102%) contrast(102%) !important;
      }

      .floating-btn:hover .floating-icon {
        transform: scale(1.1) !important;
      }

      /* Services Section Mobile Styles */
      .services-container {
        gap: 15px !important;
      }

      .services-container > div {
        height: 60px !important;
        font-size: 20px !important;
      }

      .services-container img {
        height: 100% !important;
      }

      h2 {
        font-size: 28px !important;
        margin-bottom: 20px !important;
      }
    }
    
    @media (max-width: 480px) {
      .hero-title {
        font-size: 28px !important;
      }
      .section-title {
        font-size: 24px !important;
        margin-bottom: 20px !important;
      }
      .stats-grid {
        grid-template-columns: 1fr !important;
      }
      .header-title {
        font-size: 14px !important;
      }

      .mobile-form-first {
        padding: 0 !important;
        min-height: 100vh !important;
      }

      .mobile-form-container {
        padding: 20px !important;
        border-radius: 18px !important;
        max-width: 380px !important;
        width: calc(100% - 10px) !important;
      }

      .mobile-hero-title {
        font-size: 24px !important;
      }

      .form-title-mobile {
        font-size: 22px !important;
      }

      .form-grid-mobile {
        gap: 12px !important;
      }

      .form-input-mobile {
        padding: 12px !important;
        font-size: 15px !important;
      }

      /* Services Section Small Mobile Styles */
      .services-container {
        gap: 10px !important;
      }

      .services-container > div {
        height: 50px !important;
        font-size: 18px !important;
        padding-left: 15px !important;
      }

      .services-container img {
        height: 100% !important;
      }

      h2 {
        font-size: 24px !important;
        margin-bottom: 15px !important;
      }
    }
    
    @media (min-width: 769px) {
      .mobile-menu-icon {
        display: none !important;
      }
      
      .mobile-menu {
        display: none !important;
      }
      
      .desktop-get-quote {
        display: inline-block !important;
      }

      /* Desktop Floating Buttons */
      .floating-buttons {
        position: fixed !important;
        bottom: 30px !important;
        right: 30px !important;
        display: flex !important;
        flex-direction: column !important;
        gap: 15px !important;
        z-index: 1000 !important;
      }

      .floating-btn {
        width: 65px !important;
        height: 65px !important;
        border-radius: 50% !important;
        border: none !important;
        display: flex !important;
        align-items: center !important;
        justify-content: center !important;
        font-size: 26px !important;
        color: white !important;
        text-decoration: none !important;
        box-shadow: 0 6px 25px rgba(0,0,0,0.2) !important;
        transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1) !important;
        cursor: pointer !important;
      }

      .floating-btn:hover {
        transform: scale(1.15) translateY(-3px) !important;
        box-shadow: 0 10px 35px rgba(0,0,0,0.3) !important;
      }

      .floating-btn:active {
        transform: scale(0.95) !important;
      }

      .call-btn {
        background: linear-gradient(135deg, #FFF222, #FFE55C) !important;
        border: 2px solid #333 !important;
      }

      .whatsapp-btn {
        background: linear-gradient(135deg, #25D366, #128C7E) !important;
      }

      .floating-btn-text {
        position: absolute !important;
        right: 75px !important;
        background: rgba(0,0,0,0.8) !important;
        color: white !important;
        padding: 10px 15px !important;
        border-radius: 25px !important;
        font-size: 14px !important;
        white-space: nowrap !important;
        opacity: 0 !important;
        transform: translateX(15px) !important;
        transition: all 0.3s ease !important;
        pointer-events: none !important;
        font-weight: 600 !important;
      }

      .floating-btn:hover .floating-btn-text {
        opacity: 1 !important;
        transform: translateX(0) !important;
      }

      .floating-icon {
        width: 32px !important;
        height: 32px !important;
        transition: transform 0.3s ease !important;
      }

      .call-btn .floating-icon {
        filter: brightness(0) !important;
      }

      .whatsapp-btn .floating-icon {
        filter: invert(100%) sepia(100%) saturate(0%) hue-rotate(288deg) brightness(102%) contrast(102%) !important;
      }

      .floating-btn:hover .floating-icon {
        transform: scale(1.15) !important;
      }
    }
  `;

  return (
    <>
      <style>{responsiveStyles}</style>
      <div style={{ fontFamily: 'Arial, sans-serif' }}>
        {/* Header */}
        <header style={{
          position: 'sticky',
          top: 0,
          backgroundColor: 'white',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
          zIndex: 1000,
          padding: '10px 20px'
        }}>
          <div style={{
            maxWidth: '1200px',
            margin: '0 auto',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <img
                src="/my-logo.png"
                alt="Cheapest Car Recovery UK Logo"
                style={{ height: '40px', width: 'auto' }}
              />
              <div className="header-title" style={{
                fontSize: '20px',
                fontWeight: 'bold',
                color: '#333'
              }}>
                CHEAPEST CAR RECOVERY UK
              </div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
              {/* Desktop Navigation */}
              <nav className="header-nav" style={{ display: 'flex', gap: '20px' }}>
                <a href="/" style={{
                  color: '#000000',
                  textDecoration: 'none',
                  fontWeight: 'bold',
                  fontSize: '16px'
                }}>Home</a>
                <a href="/about-us" style={{
                  color: '#333',
                  textDecoration: 'none',
                  fontWeight: '500',
                  fontSize: '16px'
                }}>About Us</a>
              </nav>

              {/* Mobile Menu Icon */}
              <button
                className="mobile-menu-icon"
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                style={{
                  display: 'none',
                  background: 'none',
                  border: 'none',
                  fontSize: '24px',
                  cursor: 'pointer',
                  padding: '8px'
                }}
              >
                {mobileMenuOpen ? '✕' : '☰'}
              </button>

              {/* Get Quote Button */}
              <a
                href="#quote-form"
                className="desktop-get-quote"
                style={{
                  backgroundColor: '#FFF222',
                  color: '#333',
                  padding: '10px 20px',
                  border: 'none',
                  borderRadius: '5px',
                  fontSize: '16px',
                  fontWeight: 'bold',
                  textDecoration: 'none',
                  display: 'inline-block'
                }}
              >
                Get Quote
              </a>
            </div>
          </div>

          {/* Mobile Menu */}
          <div className={`mobile-menu ${mobileMenuOpen ? 'open' : ''}`}>
            <nav style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
              <a
                href="/"
                onClick={() => setMobileMenuOpen(false)}
                style={{
                  color: '#000000',
                  textDecoration: 'none',
                  fontWeight: 'bold',
                  fontSize: '18px',
                  padding: '10px 0',
                  borderBottom: '1px solid #eee'
                }}
              >
                Home
              </a>
              <a
                href="/about-us"
                onClick={() => setMobileMenuOpen(false)}
                style={{
                  color: '#333',
                  textDecoration: 'none',
                  fontWeight: '500',
                  fontSize: '18px',
                  padding: '10px 0',
                  borderBottom: '1px solid #eee'
                }}
              >
                About Us
              </a>
              <a
                href="#quote-form"
                className="desktop-get-quote"
                style={{
                  backgroundColor: '#FFF222',
                  color: '#333',
                  padding: '10px 20px',
                  border: 'none',
                  borderRadius: '5px',
                  fontSize: '16px',
                  fontWeight: 'bold',
                  textDecoration: 'none',
                  display: 'inline-block'
                }}
              >
                Get Quote
              </a>
            </nav>
          </div>
        </header>

        {/* Main Content Container */}
        <main style={{ display: 'flex', flexDirection: 'column' }}>

          {/* Hero Section */}
          <section className="hero-mobile-order" style={{
            backgroundColor: '#FFF222',
            padding: '60px 20px',
            minHeight: '400px'
          }}>
            <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
              <div className="hero-content" style={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                <div className="hero-text" style={{ flex: 1, paddingRight: '40px' }}>
                  <h1 className="hero-title" style={{
                    fontSize: '48px',
                    fontWeight: 'bold',
                    color: '#333',
                    marginBottom: '20px',
                    lineHeight: '1.2'
                  }}>
                    Cheapest Car Recovery in UK
                  </h1>
                  <p style={{
                    fontSize: '18px',
                    color: '#333',
                    marginBottom: '20px',
                    lineHeight: '1.6'
                  }}>
                    Professional 24/7 breakdown recovery services across the UK.
                    Fast response times, affordable rates, and expert technicians ready to help.
                  </p>
                </div>
                <div style={{ flex: 1 }}>
                  <img
                    src="https://i.pinimg.com/736x/ca/dd/d8/caddd8fba45b8a8d4c440ce6425514da.jpg"
                    alt="Car Recovery Service Birmingham"
                    style={{
                      width: '100%',
                      height: '300px',
                      objectFit: 'cover',
                      borderRadius: '10px'
                    }}
                  />
                </div>
              </div>
            </div>
          </section>

          {/* Quote Form Section */}
          <section id="quote-form" className="mobile-form-first" style={{
            padding: '60px 20px',
            backgroundColor: '#f8f9fa'
          }}>
            <div style={{ maxWidth: '800px', margin: '0 auto' }}> {/* Mobile Hero Heading */}
              <div className="mobile-hero-heading">
                <h1 className="mobile-hero-title">Cheapest Car Recovery UK</h1>
                <p className="mobile-hero-subtitle">24/7 Emergency Breakdown Recovery</p>
              </div>

              {/* Recovery Service Image */}
              <div className="mobile-hero-image-container" style={{
                textAlign: 'center',
                marginBottom: '30px',
                display: 'flex',
                justifyContent: 'center',
                padding: '0 10px'
              }}>
                <img
                  src={firstImage}
                  alt="Car Recovery Service"
                  style={{
                    width: '100%',
                    maxWidth: '450px',
                    aspectRatio: '1/1',
                    objectFit: 'cover',
                    borderRadius: '15px',
                    boxShadow: '0 12px 24px rgba(0, 0, 0, 0.15)'
                  }}
                />
              </div>

              <div className="mobile-form-container" style={{
                backgroundColor: 'white',
                padding: '40px',
                borderRadius: '12px',
                boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
              }}>
                <h2 className="form-title-mobile" style={{
                  fontSize: '32px',
                  fontWeight: 'bold',
                  color: '#333',
                  marginBottom: '10px',
                  textAlign: 'center'
                }}>
                  Get Quote Instantly
                </h2>
                <p className="form-subtitle-mobile" style={{
                  fontSize: '16px',
                  color: '#666',
                  textAlign: 'center',
                  marginBottom: '30px'
                }}>
                  Fast & affordable recovery across UK
                </p>

                {error && (
                  <div style={{
                    backgroundColor: '#f8d7da',
                    color: '#721c24',
                    padding: '15px',
                    borderRadius: '8px',
                    marginBottom: '20px',
                    border: '1px solid #f5c6cb'
                  }}>
                    {error}
                  </div>
                )}

                <form onSubmit={handleSubmit}>
                  <div className="form-grid" style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
                    gap: '20px',
                    marginBottom: '30px'
                  }}>
                    <div>
                      <label className="form-label-mobile" style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold', color: '#333' }}>Recovery Type</label>
                      <select
                        value={formData.recoveryType}
                        onChange={(e) => handleInputChange('recoveryType', e.target.value)}
                        className="form-input-mobile"
                        style={{
                          width: '100%',
                          padding: '12px',
                          border: '1px solid #ddd',
                          borderRadius: '4px',
                          fontSize: '16px'
                        }}
                      >
                        <option value="Breakdown Recovery">Breakdown Recovery</option>
                        <option value="Accident Recovery">Accident Recovery</option>
                        <option value="Emergency Recovery">Emergency Recovery</option>
                        <option value="Van Recovery">Van Recovery</option>
                        <option value="Motorway Recovery">Motorway Recovery</option>
                        <option value="Caravan Recovery">Caravan Recovery</option>
                        <option value="Motorbike Recovery">Motorbike Recovery</option>
                        <option value="Jump Start">Jump Start</option>
                        <option value="Flat Tyre">Flat Tyre</option>
                        <option value="Stuck Vehicle">Stuck Vehicle</option>
                        <option value="Refueling">Refueling</option>
                        <option value="Mobile Mechanic">Mobile Mechanic</option>
                      </select>
                    </div>

                    <div>
                      <label className="form-label-mobile" style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold', color: '#333' }}>From Postcode</label>
                      <input
                        type="text"
                        placeholder="Enter Postcode"
                        value={formData.collectionPostcode}
                        onChange={(e) => handleInputChange('collectionPostcode', e.target.value)}
                        className="form-input-mobile"
                        style={{
                          width: '100%',
                          padding: '12px',
                          border: '1px solid #ddd',
                          borderRadius: '4px',
                          fontSize: '16px'
                        }}
                      />
                    </div>

                    <div>
                      <label className="form-label-mobile" style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold', color: '#333' }}>To Postcode</label>
                      <input
                        type="text"
                        placeholder="Delivery postcode"
                        value={formData.deliveryPostcode}
                        onChange={(e) => handleInputChange('deliveryPostcode', e.target.value)}
                        className="form-input-mobile"
                        style={{
                          width: '100%',
                          padding: '12px',
                          border: '1px solid #ddd',
                          borderRadius: '4px',
                          fontSize: '16px'
                        }}
                      />
                    </div>

                    <div>
                      <label className="form-label-mobile" style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold', color: '#333' }}>Phone Number</label>
                      <input
                        type="tel"
                        placeholder="Your contact number"
                        value={formData.phoneNumber}
                        onChange={(e) => handleInputChange('phoneNumber', e.target.value)}
                        className="form-input-mobile"
                        style={{
                          width: '100%',
                          padding: '12px',
                          border: '1px solid #ddd',
                          borderRadius: '4px',
                          fontSize: '16px'
                        }}
                      />
                    </div>

                    <div>
                      <label className="form-label-mobile" style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold', color: '#333' }}>Registration Number</label>
                      <input
                        type="text"
                        placeholder="Vehicle reg number"
                        value={formData.regNumber}
                        onChange={(e) => handleInputChange('regNumber', e.target.value)}
                        className="form-input-mobile"
                        style={{
                          width: '100%',
                          padding: '12px',
                          border: '1px solid #ddd',
                          borderRadius: '4px',
                          fontSize: '16px'
                        }}
                      />
                    </div>

                    <div>
                      <label className="form-label-mobile" style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold', color: '#333' }}>Vehicle Make</label>
                      <input
                        type="text"
                        placeholder="e.g. Ford, BMW, Audi"
                        value={formData.make}
                        onChange={(e) => handleInputChange('make', e.target.value)}
                        className="form-input-mobile"
                        style={{
                          width: '100%',
                          padding: '12px',
                          border: '1px solid #ddd',
                          borderRadius: '4px',
                          fontSize: '16px'
                        }}
                      />
                    </div>

                    <div>
                      <label className="form-label-mobile" style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold', color: '#333' }}>Vehicle Model</label>
                      <input
                        type="text"
                        placeholder="e.g. Focus, 3 Series"
                        value={formData.model}
                        onChange={(e) => handleInputChange('model', e.target.value)}
                        className="form-input-mobile"
                        style={{
                          width: '100%',
                          padding: '12px',
                          border: '1px solid #ddd',
                          borderRadius: '4px',
                          fontSize: '16px'
                        }}
                      />
                    </div>

                    <div>
                      <label className="form-label-mobile" style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold', color: '#333' }}>Date Needed</label>
                      <input
                        type="date"
                        min={new Date().toISOString().split('T')[0]}
                        value={formData.date}
                        onChange={(e) => handleInputChange('date', e.target.value)}
                        className="form-input-mobile"
                        style={{
                          width: '100%',
                          padding: '12px',
                          border: '1px solid #ddd',
                          borderRadius: '4px',
                          fontSize: '16px'
                        }}
                      />
                    </div>
                  </div>

                  <div style={{ textAlign: 'center' }}>
                    <button
                      type="submit"
                      disabled={loading}
                      className="submit-button-mobile"
                      style={{
                        backgroundColor: '#FFF222',
                        color: '#333',
                        padding: '15px 40px',
                        border: 'none',
                        borderRadius: '8px',
                        fontSize: '18px',
                        fontWeight: 'bold',
                        cursor: loading ? 'not-allowed' : 'pointer',
                        opacity: loading ? 0.7 : 1,
                        transition: 'all 0.3s ease'
                      }}
                    >
                      {loading ? 'Submitting...' : 'Get Quote Now'}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </section>

          {/* Why Choose Us Section */}
          <section style={{
            padding: '0 20px',
            backgroundColor: 'white'
          }}>
            <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
              <div className="section-flex" style={{ display: 'flex', alignItems: 'center' }}>
                <div className="section-text" style={{ flex: 1, paddingRight: '40px' }}>
                  <h2 className="section-title" style={{ fontSize: '36px', marginBottom: '20px', color: '#333' }}>
                    Why Choose Our Car Recovery Service?
                  </h2>
                  <p style={{ fontSize: '16px', lineHeight: '1.6', color: '#666', marginBottom: '20px' }}>
                    <strong>Cheapest Car Recovery UK</strong> provides affordable vehicle recovery services 24/7 throughout the UK.
                    Our professional technicians use specialized equipment to ensure your vehicle is recovered safely and efficiently.
                  </p>
                  <p style={{ fontSize: '16px', lineHeight: '1.6', color: '#666' }}>
                    Whether you need breakdown recovery, accident recovery, or roadside assistance anywhere in the UK,
                    our experienced team is here to help you get back on the road quickly and affordably.
                  </p>
                </div>
                <div className="section-image" style={{
                  flex: 1,
                  position: 'relative',
                  borderRadius: '20px',
                  overflow: 'hidden',
                  boxShadow: '0 20px 40px rgba(0,0,0,0.1)',
                  height: '500px'
                }}>
                  <img
                    src="https://i.pinimg.com/736x/82/97/ba/8297ba8bbd67aa8d47bf8bf2056daeb2.jpg"
                    alt="Professional Car Recovery Team Birmingham"
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover'
                    }}
                  />
                  <div style={{
                    position: 'absolute',
                    bottom: 0,
                    left: 0,
                    right: 0,
                    background: 'linear-gradient(to top, rgba(0,0,0,0.7) 0%, transparent 100%)',
                    padding: '40px 20px 20px',
                    color: 'white',
                    textAlign: 'center'
                  }}>
                    <div style={{ fontSize: '24px', fontWeight: 'bold', marginBottom: '5px' }}>1000+</div>
                    <div style={{ fontSize: '16px' }}>Satisfied Customers</div>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Services Section */}
          <section style={{
            padding: '20px 20px',
            backgroundColor: '#fff'
          }}>
            <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
              <h2 style={{
                fontSize: '32px',
                textAlign: 'left',
                marginBottom: '30px',
                color: '#333',
                fontWeight: 'bold'
              }}>
                Our Recovery Services
              </h2>
              <div className="services-container" style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(3, 1fr)',
                gap: '20px'
              }}>
                {services.map((service, index) => (
                  <div
                    key={index}
                    className="service-card scroll-animate-left"
                    onClick={() => navigate(`/service/${service.slug}`)}
                    style={{
                      position: 'relative',
                      borderRadius: '8px',
                      overflow: 'hidden',
                      height: '350px',
                      cursor: 'pointer',
                      transition: 'transform 0.3s ease, opacity 0.6s ease, transform 0.6s ease',
                      padding: 0,
                      margin: 0,
                      display: 'block',
                      fontSize: 0
                    }}
                  >
                    <img
                      src={service.image}
                      alt={service.title}
                      style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                        objectPosition: 'center'
                      }}
                    />
                    <div style={{
                      position: 'absolute',
                      bottom: 0,
                      left: 0,
                      right: 0,
                      backgroundColor: '#FFF222',
                      padding: '10px 15px',
                      color: 'black',
                      fontSize: '20px',
                      fontWeight: 'bold',
                      textAlign: 'center',
                      lineHeight: '1.2',
                      margin: 0 // ADD THIS
                    }}>
                      {service.title}
                    </div>

                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Add responsive styles */}
          <style>
            {`
              @media (max-width: 1024px) {
                .services-container {
                  grid-template-columns: repeat(2, 1fr) !important;
                }
              }

              @media (max-width: 768px) {
                .services-container {
                  grid-template-columns: 1fr !important;
                  gap: 15px !important;
                }

                .services-container > div {
                  height: 300px !important;
                  margin: 0 !important;
                  padding: 0 !important;
                }

                .services-container > div > div {
                  font-size: 18px !important;
                  padding: 8px 10px !important;
                  line-height: 1 !important;
                }

                h2 {
                  font-size: 28px !important;
                  margin-bottom: 20px !important;
                }
              }

              @media (max-width: 480px) {
                .services-container > div {
                  height: 250px !important;
                  margin: 0 !important;
                  padding: 0 !important;
                }

                .services-container > div > div {
                  font-size: 16px !important;
                  padding: 8px !important;
                  line-height: 1 !important;
                }

                h2 {
                  font-size: 24px !important;
                  margin-bottom: 15px !important;
                }
              }

              .services-container > div {
                box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
                transform: translateY(0);
                transition: all 0.3s ease;
                margin: 0;
                padding: 0;
              }

              .services-container > div:hover {
                transform: translateY(-5px) !important;
                box-shadow: 0 6px 12px rgba(0, 0, 0, 0.15) !important;
              }

              .services-container > div > img {
                transition: transform 0.3s ease;
              }

              .services-container > div:hover > img {
                transform: scale(1.05);
              }
            `}
          </style>

          {/* About Section */}
          <section style={{
            padding: '60px 20px',
            backgroundColor: 'white'
          }}>
            <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
              <div className="section-flex" style={{ display: 'flex', alignItems: 'center' }}>
                <div className="section-image" style={{ flex: 1, paddingRight: '40px' }}>
                  <img
                    src="https://i.pinimg.com/736x/84/d2/ad/84d2ad005da094db9b53714b869563cb.jpg"
                    alt="Car Recovery Vehicle Birmingham"
                    style={{
                      width: '100%',
                      height: '400px',
                      objectFit: 'cover',
                      borderRadius: '10px'
                    }}
                  />
                </div>
                <div className="section-text" style={{ flex: 1, paddingLeft: '40px' }}>
                  <h2 className="section-title" style={{ fontSize: '36px', marginBottom: '20px', color: '#333' }}>
                    About Cheapest Car Recovery UK
                  </h2>
                  <p style={{ fontSize: '16px', lineHeight: '1.8', color: '#666', marginBottom: '20px' }}>
                    <strong>Cheapest Car Recovery UK</strong> is your trusted partner for professional vehicle recovery
                    services throughout the United Kingdom. We specialize in breakdown recovery,
                    accident recovery, roadside assistance, and emergency vehicle transportation.
                  </p>
                  <p style={{ fontSize: '16px', lineHeight: '1.8', color: '#666' }}>
                    With our fleet of modern recovery vehicles and team of experienced technicians, we provide swift response times
                    and expert service to get you back on the road safely. Our 24/7 availability and commitment to customer satisfaction
                    make us the UK's preferred choice for affordable car recovery services.
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* Vision & Mission Section */}
          <section style={{
            padding: '60px 20px',
            backgroundColor: '#333',
            color: 'white'
          }}>
            <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
              <h2 className="section-title" style={{ fontSize: '36px', textAlign: 'center', marginBottom: '40px' }}>
                Our Vision & Mission
              </h2>
              <div className="vision-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))', gap: '40px' }}>
                <div>
                  <h3 style={{ fontSize: '24px', marginBottom: '20px', color: '#FFF222' }}>Our Vision</h3>
                  <p style={{ fontSize: '16px', lineHeight: '1.8' }}>
                    To become the UK's most trusted and affordable <strong>car recovery service</strong>, setting the standard
                    for professional vehicle recovery nationwide. We aim to be the first choice for drivers needing
                    reliable, fast, and cost-effective recovery services.
                  </p>
                </div>
                <div>
                  <h3 style={{ fontSize: '24px', marginBottom: '20px', color: '#FFF222' }}>Our Mission</h3>
                  <p style={{ fontSize: '16px', lineHeight: '1.8' }}>
                    Our mission is to provide 24/7 professional <strong>vehicle recovery services</strong> across the UK,
                    including <strong>breakdown recovery</strong>, <strong>accident recovery</strong>, <strong>roadside assistance</strong>,
                    and emergency vehicle transportation at the most competitive rates.
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* Professional Stats Section */}
          <section style={{
            padding: '60px 20px',
            backgroundColor: 'white'
          }}>
            <div style={{ maxWidth: '1200px', margin: '0 auto', textAlign: 'center' }}>
              <h2 className="section-title" style={{ fontSize: '36px', marginBottom: '20px', color: '#333' }}>
                Professional Car Recovery Service
              </h2>
              <p style={{ fontSize: '18px', marginBottom: '40px', maxWidth: '800px', margin: '0 auto 40px auto', color: '#666' }}>
                Serving the UK with professional vehicle recovery services. Our experienced team handles
                all types of breakdowns, accidents, and roadside emergencies with expertise and care.
              </p>
              <div className="stats-grid" style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                gap: '40px'
              }}>
                <div style={{
                  padding: '20px',
                  backgroundColor: '#f8f9fa',
                  borderRadius: '10px'
                }}>
                  <div style={{ fontSize: '48px', fontWeight: 'bold', color: '#333' }}>
                    <AnimatedCounter end={50} suffix="+" />
                  </div>
                  <div style={{ fontSize: '18px', marginTop: '10px', color: '#666' }}>Recovery Vehicles</div>
                </div>
                <div style={{
                  padding: '20px',
                  backgroundColor: '#f8f9fa',
                  borderRadius: '10px'
                }}>
                  <div style={{ fontSize: '48px', fontWeight: 'bold', color: '#333' }}>
                    <AnimatedCounter end={1000} suffix="+" />
                  </div>
                  <div style={{ fontSize: '18px', marginTop: '10px', color: '#666' }}>Satisfied Customers</div>
                </div>
                <div style={{
                  padding: '20px',
                  backgroundColor: '#f8f9fa',
                  borderRadius: '10px'
                }}>
                  <div style={{ fontSize: '48px', fontWeight: 'bold', color: '#333' }}>24/7</div>
                  <div style={{ fontSize: '18px', marginTop: '10px', color: '#666' }}>Emergency Service</div>
                </div>
                <div style={{
                  padding: '20px',
                  backgroundColor: '#f8f9fa',
                  borderRadius: '10px'
                }}>
                  <div style={{ fontSize: '48px', fontWeight: 'bold', color: '#333' }}>
                    <AnimatedCounter end={120} suffix="+" />
                  </div>
                  <div style={{ fontSize: '18px', marginTop: '10px', color: '#666' }}>Team Members</div>
                </div>
              </div>
            </div>
          </section>

          {/* Trust Section */}
          <section style={{
            padding: '60px 20px',
            backgroundColor: '#f8f9fa'
          }}>
            <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
              <h2 className="section-title" style={{ fontSize: '36px', textAlign: 'center', marginBottom: '20px', color: '#333' }}>
                Trust the UK's Best Car Recovery Service
              </h2>
              <p style={{ fontSize: '18px', textAlign: 'center', marginBottom: '40px', color: '#666' }}>
                When you need reliable car recovery anywhere in the UK, trust our professional team to get you back on the road safely and quickly.
              </p>
              <div className="trust-grid" style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
                gap: '30px',
                textAlign: 'center'
              }}>
                <div
                  className="scroll-animate-left"
                  style={{
                    padding: '30px',
                    backgroundColor: 'white',
                    borderRadius: '10px',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                  }}
                >
                  <div style={{
                    width: '60px',
                    height: '60px',
                    backgroundColor: '#FFF222',
                    borderRadius: '50%',
                    margin: '0 auto 20px auto',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '24px'
                  }}>
                    🕐
                  </div>
                  <h3 style={{ fontSize: '20px', marginBottom: '15px', color: '#333' }}>24/7 UK Service</h3>
                  <p style={{ fontSize: '14px', color: '#666' }}>
                    Round-the-clock car recovery services across the UK.
                    Emergency breakdown assistance available anytime, day or night.
                  </p>
                </div>
                <div
                  className="scroll-animate-left"
                  style={{
                    padding: '30px',
                    backgroundColor: 'white',
                    borderRadius: '10px',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                  }}
                >
                  <div style={{
                    width: '60px',
                    height: '60px',
                    backgroundColor: '#FFF222',
                    borderRadius: '50%',
                    margin: '0 auto 20px auto',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '24px'
                  }}>
                    ✓
                  </div>
                  <h3 style={{ fontSize: '20px', marginBottom: '15px', color: '#333' }}>Guaranteed Satisfaction</h3>
                  <p style={{ fontSize: '14px', color: '#666' }}>
                    100% customer satisfaction guarantee with every car recovery service.
                    Professional, reliable, and affordable vehicle recovery solutions.
                  </p>
                </div>
                <div
                  className="scroll-animate-left"
                  style={{
                    padding: '30px',
                    backgroundColor: 'white',
                    borderRadius: '10px',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                  }}
                >
                  <div style={{
                    width: '60px',
                    height: '60px',
                    backgroundColor: '#FFF222',
                    borderRadius: '50%',
                    margin: '0 auto 20px auto',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '24px'
                  }}>
                    👥
                  </div>
                  <h3 style={{ fontSize: '20px', marginBottom: '15px', color: '#333' }}>Professional Team</h3>
                  <p style={{ fontSize: '14px', color: '#666' }}>
                    Nationwide recovery team with extensive knowledge of UK roads.
                    Fast response times and professional vehicle recovery services.
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* Final CTA Section */}
          <section style={{
            padding: '60px 20px',
            backgroundColor: '#FFF222',
            textAlign: 'center'
          }}>
            <div style={{ maxWidth: '800px', margin: '0 auto' }}>
              <h2 style={{ fontSize: '32px', marginBottom: '20px', color: '#333' }}>
                Need Car Recovery in the UK? Contact Us Now!
              </h2>
              <p style={{ fontSize: '18px', marginBottom: '30px', color: '#333' }}>
                Professional car recovery services across the UK. Available 24/7 for breakdowns,
                accidents, and roadside emergencies. Get your instant quote now and experience the UK's most affordable
                car recovery service.
              </p>
              <a
                href="#quote-form"
                style={{
                  backgroundColor: '#333',
                  color: 'white',
                  padding: '15px 30px',
                  border: 'none',
                  borderRadius: '5px',
                  fontSize: '18px',
                  fontWeight: 'bold',
                  textDecoration: 'none',
                  display: 'inline-block',
                  marginRight: '20px',
                  marginBottom: '10px'
                }}
              >
                Get Quote Now
              </a>
              <a
                href="tel:+447432387241"
                style={{
                  backgroundColor: 'white',
                  color: '#333',
                  padding: '15px 30px',
                  border: '2px solid #333',
                  borderRadius: '5px',
                  fontSize: '18px',
                  fontWeight: 'bold',
                  textDecoration: 'none',
                  display: 'inline-block',
                  marginBottom: '10px'
                }}
              >
                Call Now: +44 7432 387241
              </a>
            </div>
          </section>

        </main>

        {/* Footer */}
        <footer style={{
          backgroundColor: '#333',
          color: 'white',
          padding: '40px 20px',
          textAlign: 'center'
        }}>
          <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '15px', marginBottom: '20px' }}>
              <img
                src="/my-logo.png"
                alt="Cheapest Car Recovery UK Logo"
                style={{ height: '50px', width: 'auto' }}
              />
              <h3 style={{ fontSize: '24px', margin: 0 }}>
                Cheapest Car Recovery UK - Contact Us
              </h3>
            </div>
            <div style={{ fontSize: '18px', lineHeight: '2', marginBottom: '20px' }}>
              <div>📧 admin@cheapestcarrecovery.uk</div>
              <div>+44 7432 387241</div>
              <div>Serving the UK</div>
            </div>
            <p style={{ fontSize: '14px', color: '#ccc', marginTop: '20px' }}>
              © 2024 Cheapest Car Recovery UK. Professional vehicle recovery services across the UK.
              Available 24/7 for emergency breakdown recovery.
            </p>
          </div>
        </footer>

        {/* Floating Action Buttons */}
        <div className="floating-buttons">
          <a
            href="tel:+447432387241"
            className="floating-btn call-btn"
            title="Call Now"
          >
            <img
              src={phoneIcon}
              alt="Phone"
              className="floating-icon"
            />
            <span className="floating-btn-text">Call Now</span>
          </a>
          <a
            href="https://wa.me/447432387241?text=Hi%2C%20I%20need%20car%20recovery%20service"
            target="_blank"
            rel="noopener noreferrer"
            className="floating-btn whatsapp-btn"
            title="WhatsApp Chat"
          >
            <img
              src={whatsappIcon}
              alt="WhatsApp"
              className="floating-icon"
            />
            <span className="floating-btn-text">WhatsApp</span>
          </a>
        </div>
      </div>
    </>
  );
};

export default Homepage; 